import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import {
  S3PAY_FUNCTIONS_BASE,
  fetchS3PayPublicConfig,
  isS3PayDepositAvailableForCode,
  normalizeS3PayVisibleCodes
} from '@/utils/s3payPublicConfig'
import { isPaymentBankBlocked } from '@/utils/paymentBankAvailability'

const FINAL_STATUSES = ['SUCCESS', 'FAILED', 'EXPIRED', 'CANCELLED', 'CREATE_FAILED', 'MANUAL_REVIEW']
const TRUE_WALLET_BANK_KEYS = ['truewallet', 'truemoney', 'truemoneywallet', 'ทรูวอลเลท', 'ทรูมันนี่']
const S3PAY_DEPOSIT_TIMEOUT_MS = 15 * 60 * 1000

function normalizeAccountBank (value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
}

function requestId () {
  return `s3pay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

function normalizeS3PayShowUrl (value) {
  try {
    const paymentUrl = new URL(String(value || '').trim())
    return paymentUrl.protocol === 'https:' ? paymentUrl.href : ''
  } catch (error) {
    return ''
  }
}

function normalizeS3PayQrData (value) {
  const candidates = [
    value && value.qr_data,
    value && value.qr && value.qr.qr_data,
    value && value.data && value.data.qr_data,
    value && value.data && value.data.qr && value.data.qr.qr_data
  ]
  const qrData = candidates.find(candidate => String(candidate || '').trim())
  return String(qrData || '').trim()
}

function normalizeS3PayExpiresAt (value) {
  const candidates = [
    value && value.expires_at,
    value && value.expired_at,
    value && value.expiresAt,
    value && value.expiredAt
  ]
  for (const candidate of candidates) {
    if (!candidate) continue
    if (typeof candidate.toMillis === 'function') return candidate.toMillis()
    if (typeof candidate.toDate === 'function') return candidate.toDate().getTime()
    const numeric = Number(candidate)
    if (Number.isFinite(numeric) && numeric > 0) return numeric < 100000000000 ? numeric * 1000 : numeric
    const parsed = Date.parse(String(candidate))
    if (Number.isFinite(parsed)) return parsed
  }
  return 0
}

export default {
  data () {
    return {
      s3PayConfig: {
        enabled: false,
        depositEnabled: false,
        depositMin: 300,
        depositMax: 100000,
        depositPaytype: 'remit_qr',
        allowedPaytypes: ['remit_qr'],
        visibleToCodes: [],
        blockedBanks: []
      },
      s3PayConfigLoaded: false,
      s3PaySelectedPaytype: 'remit_qr',
      s3PayTransaction: null,
      s3PayOrderId: '',
      s3PayShowUrl: '',
      s3PayPaymentOpenedAt: null,
      s3PayPaymentWindow: null,
      s3PayPopupBlocked: false,
      s3PayStatusTimer: null,
      s3PayStatusChecking: false,
      s3PayCompletionHandled: false
    }
  },
  computed: {
    hasActiveS3PayDeposit () {
      return Boolean(
        this.s3PayOrderId &&
        this.s3PayTransaction &&
        (this.s3PayShowUrl || normalizeS3PayQrData(this.s3PayTransaction))
      )
    },
    s3PayDepositRangeText () {
      return `${Number(this.s3PayConfig.depositMin || 300).toLocaleString()}-${Number(this.s3PayConfig.depositMax || 100000).toLocaleString()}`
    },
    s3PayVisibleToUser () {
      const visibleToCodes = normalizeS3PayVisibleCodes(this.s3PayConfig.visibleToCodes)
      const userCode = String((this.user && this.user.code) || '').trim().toUpperCase()
      return visibleToCodes.includes('*') || Boolean(userCode && visibleToCodes.includes(userCode))
    },
    s3PayDepositAvailableToUser () {
      return isS3PayDepositAvailableForCode(this.s3PayConfig, this.user && this.user.code)
    },
    s3PaySelectedDepositAccount () {
      const approvedAccounts = typeof this.getApprovedAccounts === 'function' ? this.getApprovedAccounts() : []
      return this.selectedDepositAccount || approvedAccounts[0] || null
    },
    s3PayAvailableForSelectedAccount () {
      return !this.isS3PayBlockedDepositAccount(this.s3PaySelectedDepositAccount)
    }
  },
  watch: {
    'user.key': {
      immediate: true,
      handler (userKey) {
        if (!userKey) return
        this.loadS3PayConfig()
        this.restoreS3PayDeposit()
      }
    }
  },
  mounted () {
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.handleS3PayPageResume)
      window.addEventListener('pageshow', this.handleS3PayPageResume)
    }
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.handleS3PayPageResume)
  },
  beforeDestroy () {
    this.stopS3PayStatusPolling()
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.handleS3PayPageResume)
      window.removeEventListener('pageshow', this.handleS3PayPageResume)
    }
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.handleS3PayPageResume)
  },
  methods: {
    async s3PayHeaders () {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบใหม่')
      const token = await currentUser.getIdToken()
      return { Authorization: `Bearer ${token}` }
    },
    async loadS3PayConfig () {
      try {
        const publicConfig = await fetchS3PayPublicConfig()
        const config = { ...this.s3PayConfig, ...publicConfig }
        const configuredPaytype = String(config.depositPaytype || '').trim().toLowerCase()
        const fallbackPaytype = Array.isArray(config.allowedPaytypes)
          ? config.allowedPaytypes.find(paytype => ['remit', 'remit_qr'].includes(paytype))
          : ''
        const depositPaytype = ['remit', 'remit_qr'].includes(configuredPaytype)
          ? configuredPaytype
          : (fallbackPaytype || 'remit_qr')
        this.s3PayConfig = { ...config, depositPaytype, allowedPaytypes: [depositPaytype] }
        this.s3PaySelectedPaytype = depositPaytype
      } catch (error) {
        console.warn('S3Pay public config unavailable:', error && error.message ? error.message : error)
      } finally {
        this.s3PayConfigLoaded = true
      }
    },
    async refreshS3PayDepositAvailability () {
      await this.loadS3PayConfig()
      return this.s3PayDepositAvailableToUser
    },
    isS3PayAmountAllowed (amount) {
      const value = Number(amount || 0)
      const min = Number(this.s3PayConfig.depositMin || 300)
      const max = Number(this.s3PayConfig.depositMax || 100000)
      return Number.isInteger(value) && value >= min && (max <= 0 || value <= max)
    },
    isS3PayBlockedDepositAccount (account) {
      const bankKey = normalizeAccountBank(account && (account.bank || account.bank_name))
      return TRUE_WALLET_BANK_KEYS.includes(bankKey) ||
        isPaymentBankBlocked(this.s3PayConfig.blockedBanks, account)
    },
    async tryS3PayDeposit (selectedAccount) {
      if (!this.s3PayConfigLoaded) await this.loadS3PayConfig()
      if (this.isS3PayBlockedDepositAccount(selectedAccount)) return false
      if (!this.s3PayConfig.enabled || !this.s3PayConfig.depositEnabled || !this.s3PayVisibleToUser) return false
      const amount = Number.parseInt(this.inputDepositAmount, 10) || 0
      if (!this.isS3PayAmountAllowed(amount)) throw new Error(`ไม่สามารถสร้างการฝากได้ ตอนนี้ระบบรองรับยอดฝาก ${this.s3PayDepositRangeText} บาท ต่อรายการเท่านั้น`)
      const paytype = this.s3PayConfig.depositPaytype || this.s3PayConfig.allowedPaytypes[0] || 'remit_qr'
      this.s3PaySelectedPaytype = paytype

      let response
      try {
        response = await axios.post(`${S3PAY_FUNCTIONS_BASE}/s3PayCreateDeposit`, {
          userKey: this.user.key,
          amount,
          account: selectedAccount,
          bonus: this.getDepositBonusRequested(),
          clientRequestId: requestId()
        }, { headers: await this.s3PayHeaders(), timeout: 45000 })
      } catch (error) {
        throw new Error(this.s3PayErrorMessage(error))
      }
      const data = response.data && response.data.data
      if (!response.data || !response.data.success || !data || !data.order_id || !(data.showurl || normalizeS3PayQrData(data))) {
        throw new Error(response.data && response.data.error ? response.data.error : 'S3Pay response ไม่ครบถ้วน')
      }

      this.depositAmount = amount
      this.depositAmountWithDecimal = amount.toLocaleString('en-US')
      this.depositDecimal = '00'
      this.assignedBank = null
      this.paybridgeMatch = null
      const applied = this.applyS3PayTransaction({
        ...data,
        amount_requested: amount,
        paytype,
        status: data.status || 'PENDING',
        payment_opened_at: null,
        expires_at: normalizeS3PayExpiresAt(data) || Date.now() + S3PAY_DEPOSIT_TIMEOUT_MS
      })
      if (!applied) throw new Error('ไม่สามารถบันทึกรายการฝาก S3Pay ได้')
      this.s3PayPopupBlocked = false
      return true
    },
    s3PayErrorMessage (error) {
      const payload = error && error.response && error.response.data
      const providerMessage = payload && payload.provider && payload.provider.message
      return providerMessage || (payload && payload.error) || (error && error.message) || 'S3Pay ไม่สามารถสร้างรายการฝากได้ กรุณาลองใหม่'
    },
    s3PayStorageKey () {
      return `connextcc_s3pay_deposit_${(this.user && this.user.key) || 'guest'}`
    },
    persistS3PayDeposit (transaction) {
      if (typeof window === 'undefined' || !window.localStorage || !transaction) return
      const expiresAt = normalizeS3PayExpiresAt(transaction) ||
        normalizeS3PayExpiresAt(this.s3PayTransaction) ||
        (Number(transaction.stored_at) || Date.now()) + S3PAY_DEPOSIT_TIMEOUT_MS
      localStorage.setItem(this.s3PayStorageKey(), JSON.stringify({
        order_id: transaction.order_id || transaction.orderid || this.s3PayOrderId,
        showurl: transaction.showurl || this.s3PayShowUrl,
        qr_data: normalizeS3PayQrData(transaction),
        paytype: transaction.paytype || this.s3PaySelectedPaytype,
        amount: Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0),
        payment_opened_at: Number(transaction.payment_opened_at || this.s3PayPaymentOpenedAt || 0) || null,
        expires_at: expiresAt,
        stored_at: Date.now()
      }))
    },
    clearS3PayDepositCache () {
      if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem(this.s3PayStorageKey())
    },
    applyS3PayTransaction (transaction) {
      if (!transaction) return false
      const orderId = transaction.order_id || transaction.orderid || ''
      const showUrl = normalizeS3PayShowUrl(transaction.showurl || transaction.show_url)
      const qrData = normalizeS3PayQrData(transaction)
      if (!orderId || !(showUrl || qrData)) return false
      const expiresAt = normalizeS3PayExpiresAt(transaction) ||
        normalizeS3PayExpiresAt(this.s3PayTransaction) ||
        (Number(transaction.stored_at) || Date.now()) + S3PAY_DEPOSIT_TIMEOUT_MS
      const normalizedTransaction = { ...transaction, qr_data: qrData, expires_at: expiresAt }
      this.s3PayTransaction = { ...(this.s3PayTransaction || {}), ...normalizedTransaction }
      this.s3PayOrderId = orderId
      this.s3PayShowUrl = showUrl
      this.s3PayPaymentOpenedAt = Number(transaction.payment_opened_at || this.s3PayPaymentOpenedAt || 0) || null
      this.s3PaySelectedPaytype = transaction.paytype || this.s3PaySelectedPaytype
      this.depositAmount = Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0)
      this.depositAmountWithDecimal = Number(this.depositAmount || 0).toLocaleString('en-US')
      this.assignedBank = null
      this.persistS3PayDeposit(normalizedTransaction)
      this.startS3PayStatusPolling(orderId)
      return true
    },
    openS3PayPaymentPage (showUrl = this.s3PayShowUrl) {
      if (typeof window === 'undefined') throw new Error('ไม่สามารถเปิดหน้าชำระเงินบนอุปกรณ์นี้ได้')
      const paymentUrl = normalizeS3PayShowUrl(showUrl)
      if (!paymentUrl) throw new Error('ลิงก์หน้าชำระเงิน S3Pay ไม่ถูกต้องหรือไม่ได้ใช้ HTTPS')
      try {
        if (this.s3PayPaymentWindow && !this.s3PayPaymentWindow.closed) {
          this.s3PayPaymentWindow.focus()
          return true
        }
      } catch (error) {
        this.s3PayPaymentWindow = null
      }
      const paymentWindow = window.open('about:blank', '_blank')
      if (!paymentWindow) {
        this.s3PayPopupBlocked = true
        return false
      }
      try {
        paymentWindow.opener = null
        paymentWindow.location.replace(paymentUrl)
        this.s3PayPaymentWindow = paymentWindow
        this.s3PayPopupBlocked = false
        this.s3PayPaymentOpenedAt = Date.now()
        this.s3PayTransaction = {
          ...(this.s3PayTransaction || {}),
          payment_opened_at: this.s3PayPaymentOpenedAt
        }
        this.persistS3PayDeposit(this.s3PayTransaction)
        return true
      } catch (error) {
        try { paymentWindow.close() } catch (closeError) {}
        this.s3PayPaymentWindow = null
        this.s3PayPopupBlocked = true
        return false
      }
    },
    handleS3PayPageResume () {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      if (this.s3PayOrderId && !this.s3PayCompletionHandled) this.fetchS3PayDepositStatus(this.s3PayOrderId)
    },
    startS3PayStatusPolling (orderId) {
      if (!orderId) return
      this.stopS3PayStatusPolling()
      this.s3PayCompletionHandled = false
      const poll = () => this.fetchS3PayDepositStatus(orderId)
      poll()
      this.s3PayStatusTimer = setInterval(poll, 4000)
    },
    stopS3PayStatusPolling () {
      if (this.s3PayStatusTimer) clearInterval(this.s3PayStatusTimer)
      this.s3PayStatusTimer = null
    },
    async fetchS3PayDepositStatus (orderId) {
      if (!orderId || !(this.user && this.user.key) || this.s3PayCompletionHandled || this.s3PayStatusChecking) return null
      this.s3PayStatusChecking = true
      try {
        const response = await axios.post(`${S3PAY_FUNCTIONS_BASE}/s3PayGetDepositStatus`, {
          userKey: this.user.key,
          orderId
        }, { headers: await this.s3PayHeaders(), timeout: 15000 })
        const transaction = response.data && response.data.data
        if (!response.data || !response.data.success || !transaction) return null
        this.s3PayTransaction = { ...(this.s3PayTransaction || {}), ...transaction }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') this.handleS3PayDepositSuccess(transaction)
        if (['FAILED', 'EXPIRED', 'CREATE_FAILED', 'MANUAL_REVIEW'].includes(status)) this.handleS3PayDepositFailed(transaction, status)
        return transaction
      } catch (error) {
        return null
      } finally {
        this.s3PayStatusChecking = false
      }
    },
    async restoreS3PayDeposit () {
      if (typeof window === 'undefined' || !window.localStorage || !(this.user && this.user.key)) return false
      try {
        const cached = JSON.parse(localStorage.getItem(this.s3PayStorageKey()) || 'null')
        if (!cached || !cached.order_id) return false
        const response = await axios.post(`${S3PAY_FUNCTIONS_BASE}/s3PayGetDepositStatus`, {
          userKey: this.user.key,
          orderId: cached.order_id
        }, { headers: await this.s3PayHeaders(), timeout: 15000 })
        const statusData = response.data && response.data.data
        if (!response.data || !response.data.success || !statusData) {
          this.clearS3PayDepositCache()
          return false
        }
        const transaction = { ...statusData, ...cached, order_id: cached.order_id }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') {
          this.handleS3PayDepositSuccess(transaction)
          return true
        }
        if (['FAILED', 'EXPIRED', 'CREATE_FAILED', 'MANUAL_REVIEW'].includes(status)) {
          this.handleS3PayDepositFailed(transaction, status)
          return true
        }
        if (FINAL_STATUSES.includes(status)) {
          this.clearS3PayDepositCache()
          return false
        }
        return this.applyS3PayTransaction(transaction)
      } catch (error) {
        console.warn('Restore S3Pay deposit failed:', error)
        this.clearS3PayDepositCache()
        return false
      }
    },
    handleS3PayDepositSuccess (transaction) {
      if (this.s3PayCompletionHandled) return
      this.s3PayCompletionHandled = true
      this.stopS3PayStatusPolling()
      this.clearS3PayDepositCache()
      const amount = Number(transaction.actual_amount || transaction.amount_requested || this.depositAmount || 0)
      this.closeS3PayPaymentWindow()
      this.resetS3PayDepositState()
      this.hideS3PayDepositModal()
      setTimeout(() => this.$swal({ icon: 'success', title: 'ฝากเงินสำเร็จ', text: `ระบบเติมเครดิต ${amount.toLocaleString()} บาทเรียบร้อยแล้ว` }), 200)
    },
    handleS3PayDepositFailed (transaction, status) {
      if (this.s3PayCompletionHandled) return
      this.s3PayCompletionHandled = true
      this.stopS3PayStatusPolling()
      this.clearS3PayDepositCache()
      const reason = transaction.failed_reason || transaction.credit_error || transaction.s3pay_error || ''
      this.closeS3PayPaymentWindow()
      this.resetS3PayDepositState()
      this.hideS3PayDepositModal()
      setTimeout(() => this.$swal({
        icon: status === 'MANUAL_REVIEW' ? 'warning' : 'error',
        title: status === 'MANUAL_REVIEW' ? 'กำลังตรวจสอบรายการฝาก' : 'รายการฝากไม่สำเร็จ',
        text: typeof reason === 'string' ? (reason || 'กรุณาทำรายการใหม่หรือติดต่อแอดมิน') : 'กรุณาติดต่อแอดมิน'
      }), 200)
    },
    resetS3PayDepositState () {
      this.s3PayTransaction = null
      this.s3PayOrderId = ''
      this.s3PayShowUrl = ''
      this.s3PayPaymentOpenedAt = null
      this.s3PayPaymentWindow = null
      this.s3PayPopupBlocked = false
      this.s3PayStatusChecking = false
      this.s3PayCompletionHandled = false
    },
    closeS3PayPaymentWindow () {
      try {
        if (this.s3PayPaymentWindow && !this.s3PayPaymentWindow.closed) this.s3PayPaymentWindow.close()
      } catch (error) {}
      this.s3PayPaymentWindow = null
    },
    hideS3PayDepositModal () {
      if (typeof window !== 'undefined' && window.$) window.$('#depositModal').modal('hide')
    },
    async cancelS3PayDeposit (options = {}) {
      const orderId = this.s3PayOrderId
      if (orderId) await this.fetchS3PayDepositStatus(orderId)
      if (!this.s3PayOrderId) return
      if (this.$swal && !options.skipConfirmation) {
        const confirmation = await this.$swal({
          icon: 'warning',
          title: 'ยกเลิกรายการและเปลี่ยนยอดฝาก?',
          text: 'หากโอนไปแล้วอย่ายกเลิก กรุณารอระบบยืนยันรายการสำเร็จ',
          showCancelButton: true,
          confirmButtonText: 'ยืนยันยกเลิก',
          cancelButtonText: 'รอทำรายการต่อ'
        })
        if (!confirmation.isConfirmed) return
      }
      if (orderId && this.user && this.user.key) {
        try {
          const response = await axios.post(`${S3PAY_FUNCTIONS_BASE}/s3PayCancelDeposit`, { userKey: this.user.key, orderId }, { headers: await this.s3PayHeaders(), timeout: 15000 })
          if (!response.data || !response.data.success) throw new Error((response.data && response.data.error) || 'ไม่สามารถยกเลิกรายการฝากได้')
          if (response.data.alreadyCompleted) {
            await this.fetchS3PayDepositStatus(orderId)
            return
          }
        } catch (error) {
          console.warn('Close S3Pay deposit failed:', error && error.message ? error.message : error)
          if (this.$swal) {
            await this.$swal({
              icon: 'error',
              title: 'ยังไม่สามารถสร้างรายการใหม่ได้',
              text: 'ระบบปิดรายการฝากเดิมไม่สำเร็จ กรุณาลองอีกครั้ง'
            })
          }
          return
        }
      }
      this.closeS3PayPaymentWindow()
      this.stopS3PayStatusPolling()
      this.clearS3PayDepositCache()
      this.resetS3PayDepositState()
      this.depositAmount = null
      this.depositAmountWithDecimal = null
      this.inputDepositAmount = null
    }
  }
}
